import { createContext, useContext, useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import {
    callRefresh,
    RefreshRejectedError,
    refreshWithSiblingFallback,
    sessionEndedReason,
    withTransientRetry,
    type SessionEndedReason,
} from './refresh-session'
import { useApi } from '../api/hooks'
import { apiClient, setAuthToken, removeAuthToken, registerTokenRefresher } from '../api/client'
import { useNavigate } from '@tanstack/react-router'
import { usePostHog } from 'posthog-js/react'
import { apiErrorCode, apiErrorMessage } from '../lib/apiError'
import { challengeFromLogin, PENDING_CHALLENGE_KEY, type PendingChallenge } from '../lib/mfa'

interface AuthStateUser {
    uid: string
    email: string
    name: string
    properties: {
        firstName: string
        lastName: string
        language: string
    }
    // Global administrator flag. Backed by users.is_platform_admin on the
    // server. Optional on the client shape because older login payloads (or
    // cached auth state from before this field existed) may not carry it —
    // callers treat `undefined` as `false`.
    is_platform_admin?: boolean
}

interface AuthState {
    isAuthenticated: boolean
    user: AuthStateUser | null
    accessToken: string | null
    refreshToken: string | null
    orgCode: string | null
    loading: boolean
    error: string | null
    // Machine-readable counterpart of `error` (the backend's problem-detail
    // `code`, e.g. INVALID_CREDENTIALS). Consumers translate on the code and
    // only fall back to the raw `error` string for unexpected failures — the
    // raw title is never written for end users.
    errorCode: string | null
    expiresAt: Date | null
    showAuthErrorBanner: boolean
    // Why the server ended the session (the refresh's 401 code), shown by the
    // banner and carried to the login page. Null for a generic rejection.
    sessionEndedReason?: SessionEndedReason | null
    // The session is restricted to two-step verification enrollment (spec
    // 2026-10-01-03): every route but the enrollment page answers 403
    // mfa_enrollment_required until a factor is confirmed.
    mfaEnrollmentRequired: boolean
}

interface LoginCredentials {
    email: string
    password: string
    /** The organization the person came from: a hint only (spec 2026-10-14-22). */
    orgCode?: string
}

/** An organization offered to an account that has several (spec 2026-10-14-22). */
export interface OrgChoiceOption {
    code: string
    name: string
}

/** A sign-in waiting for the person to pick one of their organizations. */
export interface PendingOrgChoice {
    token: string
    organizations: OrgChoiceOption[]
}

export interface LoginResult {
    success: boolean
    error?: string
    /** Backend problem-detail code, when the failure carried one. */
    errorCode?: string
    // `defaultOrgCode` is the backend's suggestion for where to land after
    // login. Empty when the user has no memberships yet — callers should
    // then route to /no-organization.
    defaultOrgCode?: string
    hasNoOrgs?: boolean
    /** The password was right but a second factor is needed: no session yet. */
    mfa?: PendingChallenge
    /** The session only reaches the two-step verification enrollment page. */
    mfaEnrollmentRequired?: boolean
    /** The organization of the session, or of the MFA challenge. */
    orgCode?: string
    /** The hinted organization was not the account's: the session went here. */
    redirectedOrgCode?: string
    /** The account has several organizations: no session until one is chosen. */
    orgChoice?: PendingOrgChoice
}

interface LogoutOptions {
    // When true, the login page the user is sent to carries a `next` pointing
    // at the current location, so a session-expiry / programmatic logout can
    // bring them back after re-authenticating. Explicit, user-initiated
    // logouts (the account menu) leave this false and land on a clean login.
    preserveLocation?: boolean
    // When false, the user stays on the current page after the sign-out
    // instead of being sent to the login page (invitation "sign out and
    // accept"). Defaults to true.
    redirect?: boolean
}

// SSOTokens is the token bundle produced by a passkey or social-login (OAuth)
// sign-in, established outside the email/password `login` path.
export interface SSOTokens {
    access_token: string
    refresh_token: string
    user: any
    expires_in: number
    orgCode: string
    default_org_code?: string
    organizations?: unknown[]
    mfa_enrollment_required?: boolean
}

/** The `buildtory:mfa` window event the API client fires on an MFA 403. */
export const MFA_EVENT = 'buildtory:mfa'
export interface MfaEventDetail {
    code: 'mfa_required' | 'mfa_enrollment_required'
    org?: string
}

interface AuthContextType extends AuthState {
    login: (credentials: LoginCredentials) => Promise<LoginResult>
    chooseLoginOrganization: (token: string, orgCode: string) => Promise<LoginResult>
    loginWithTokens: (tokens: SSOTokens) => LoginResult
    logout: (orgCode?: string, options?: LogoutOptions) => Promise<void>
    refreshAccessToken: () => Promise<{ success: boolean }>
    dismissAuthErrorBanner: () => void
    getLoginUrl: () => string
    updateAuthUser: (user: Partial<AuthStateUser>) => void
    /**
     * Re-points the session at a renamed organization slug. Renaming the slug
     * (platform admins only, spec 2026-09-09-10 §4) changes the `/{orgCode}`
     * segment of every URL, and the session's stored one would otherwise keep
     * sending the caller back to a code that now 404s.
     */
    updateAuthOrgCode: (orgCode: string) => void
    /**
     * Installs the access token of an upgraded session: after confirming a
     * factor or passing a step-up challenge, the same session is
     * MFA-verified and no longer restricted.
     */
    applyUpgradedAccessToken: (accessToken: string, expiresIn: number) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    // Initialize state from localStorage synchronously to avoid null state
    const getInitialAuthState = (): AuthState => {
        const storedAuthState = localStorage.getItem('authState')
        if (storedAuthState) {
            try {
                const parsed = JSON.parse(storedAuthState)
                // A stored entry only counts as a session when it says so. A
                // token installed on the API client from an entry whose
                // `isAuthenticated` is not true splits the app in two: every
                // request is signed in, while every UI gate reads
                // `isAuthenticated`/`user` and believes nobody is — the
                // /organizations page then serves an authenticated,
                // sidebar-less, non-admin shell that never recovers, because
                // nothing re-reads localStorage after mount. Fail closed and
                // drop the inconsistent entry instead: a real sign-in writes a
                // complete one.
                if (parsed.isAuthenticated && parsed.accessToken) {
                    setAuthToken(parsed.accessToken)
                    return {
                        isAuthenticated: true,
                        user: parsed.user || null,
                        accessToken: parsed.accessToken,
                        refreshToken: parsed.refreshToken || null,
                        loading: false,
                        error: null,
                        errorCode: null,
                        expiresAt: new Date(parsed.expiresAt),
                        // The organization the session was opened in: a
                        // refresh must name it, the refresh row belongs to it.
                        orgCode: parsed.orgCode || 'demo',
                        showAuthErrorBanner: false,
                        mfaEnrollmentRequired: parsed.mfaEnrollmentRequired === true,
                    }
                }
                localStorage.removeItem('authState')
            } catch (error) {
                console.error('Failed to parse stored auth state:', error)
            }
        }
        return {
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            loading: false,
            error: null,
            errorCode: null,
            expiresAt: null,
            orgCode: null,
            showAuthErrorBanner: false,
            mfaEnrollmentRequired: false,
        }
    }

    const [authState, setAuthState] = useState<AuthState>(getInitialAuthState)
    const { request } = useApi()
    const navigate = useNavigate()
    const posthog = usePostHog()

    // Identify user in PostHog on initial load (restored from localStorage)
    useEffect(() => {
        if (authState.isAuthenticated && authState.user) {
            posthog.identify(authState.user.uid, {
                email: authState.user.email,
                name: authState.user.name,
                orgCode: authState.orgCode,
            })
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Save auth state to localStorage whenever it changes
    useEffect(() => {
        if (authState.isAuthenticated) {
            localStorage.setItem('authState', JSON.stringify({
                isAuthenticated: authState?.isAuthenticated || false,
                user: authState?.user || null,
                accessToken: authState?.accessToken || null,
                refreshToken: authState?.refreshToken || null,
                expiresAt: authState?.expiresAt || null,
                orgCode: authState?.orgCode || null,
                mfaEnrollmentRequired: authState?.mfaEnrollmentRequired || false,
            }))
        } else if (authState.isAuthenticated === false) {
            // Clear localStorage when explicitly logged out
            localStorage.removeItem('authState')
        }
    }, [authState])

    // Automatic token refresh - check every minute if token expires in less than 20 minutes
    useEffect(() => {
        if (!authState.isAuthenticated || !authState.expiresAt || !authState.refreshToken) {
            console.log('Not authenticated or no refresh token, skipping token refresh')
            return
        }

        const checkTokenExpiry = async () => {
            console.log('Checking token expiry', {
                expiresAt: authState.expiresAt?.toISOString(),
                refreshToken: authState.refreshToken
            })
            const now = new Date()
            const expiresAt = new Date(authState.expiresAt!)
            const timeUntilExpiry = expiresAt.getTime() - now.getTime()
            const twentyMinutesInMs = 20 * 60 * 1000 // 20 minutes in milliseconds

            // If token expires in less than 20 minutes, refresh it
            if (timeUntilExpiry < twentyMinutesInMs) {
                console.log('Token expires soon, refreshing...', {
                    expiresAt: expiresAt.toISOString(),
                    timeUntilExpiry: Math.round(timeUntilExpiry / (60 * 1000)) + ' minutes'
                })

                try {
                    await withTransientRetry(() => refreshAccessToken())
                    console.log('Token refreshed successfully')
                } catch (error) {
                    console.error('Failed to refresh token:', error)
                    // Show auth error banner instead of immediately logging out
                    setAuthState(prev => ({
                        ...prev,
                        showAuthErrorBanner: true
                    }))
                }
            }
        }

        // Check immediately
        checkTokenExpiry()

        // Set up interval to check every minute
        const interval = setInterval(checkTokenExpiry, 60 * 1000) // 60 seconds

        return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState.isAuthenticated, authState.expiresAt, authState.refreshToken])

    // completeLogin turns an answer of the account sign-in (spec
    // 2026-10-14-22) into a result: an organization choice, an MFA
    // challenge, or a session in the organization the server picked.
    const completeLogin = async (response: unknown, hintOrg: string | undefined): Promise<LoginResult> => {
        const resp = response as {
            access_token: string;
            refresh_token: string;
            user: any;
            expires_in: number;
            default_org_code?: string;
            organizations?: unknown[];
            mfa_required?: boolean;
            mfa_enrollment_required?: boolean;
            org_code?: string;
            redirected_org_code?: string;
            organization_choice_required?: boolean;
            organization_choices?: OrgChoiceOption[];
            org_choice_token?: string;
        }
        const { access_token, refresh_token, user, expires_in, default_org_code, organizations, mfa_required, mfa_enrollment_required } = resp
        const orgCode = resp.org_code || hintOrg || ''

        if (resp.organization_choice_required) {
            setAuthState(prev => ({ ...prev, loading: false, error: null, errorCode: null }))
            return {
                success: false,
                orgChoice: { token: resp.org_choice_token ?? '', organizations: resp.organization_choices ?? [] },
            }
        }

        // A second factor is needed (spec 2026-10-01-03): no session
        // yet, the login page asks for the code.
        if (mfa_required) {
            setAuthState(prev => ({ ...prev, loading: false, error: null, errorCode: null }))
            return {
                success: false,
                mfa: challengeFromLogin(resp as Parameters<typeof challengeFromLogin>[0]),
                orgCode,
                redirectedOrgCode: resp.redirected_org_code,
            }
        }

        const newAuthState = {
            isAuthenticated: true,
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            loading: false,
            error: null,
            errorCode: null,
            expiresAt: new Date(Date.now() + expires_in * 1000),
            orgCode,
            showAuthErrorBanner: false,
            mfaEnrollmentRequired: mfa_enrollment_required === true,
        }

        // Update localStorage synchronously BEFORE setting state
        // This ensures beforeLoad hooks see the updated auth state
        localStorage.setItem('authState', JSON.stringify({
            isAuthenticated: newAuthState.isAuthenticated,
            user: newAuthState.user,
            accessToken: newAuthState.accessToken,
            refreshToken: newAuthState.refreshToken,
            expiresAt: newAuthState.expiresAt,
            orgCode: newAuthState.orgCode,
            mfaEnrollmentRequired: newAuthState.mfaEnrollmentRequired,
        }))

        // Set the access token in the API client
        setAuthToken(access_token)

        // Identify user in PostHog
        posthog.identify(user.uid, {
            email: user.email,
            name: user.name,
            orgCode,
        })

        // Now update the state which will trigger re-renders
        setAuthState(newAuthState)

        // Force a micro-task to ensure state propagation
        await Promise.resolve()

        return {
            success: true,
            defaultOrgCode: default_org_code,
            hasNoOrgs: Array.isArray(organizations) && organizations.length === 0,
            mfaEnrollmentRequired: newAuthState.mfaEnrollmentRequired,
            orgCode,
            redirectedOrgCode: resp.redirected_org_code,
        }
    }

    const failLogin = (error: unknown): LoginResult => {
        // Keep the human-readable text as a last-resort fallback, but hand
        // the caller the error *code* so the login page can render its own
        // purpose-written, translated message (spec 2026-08-01-16).
        const errorCode = apiErrorCode(error)
        const errorMessage = apiErrorMessage(error, 'Login failed')
        setAuthState(prev => ({
            ...prev,
            loading: false,
            error: errorMessage,
            errorCode,
        }))
        return { success: false, error: errorMessage, errorCode: errorCode ?? undefined }
    }

    // login authenticates the account first (spec 2026-10-14-22): the
    // organization is only a hint, the server picks where the session opens.
    const login = async (credentials: LoginCredentials) => {
        setAuthState(prev => ({ ...prev, loading: true, error: null, errorCode: null }))

        try {
            const response = await request('/api/v1/auth/login', 'POST', {
                body: {
                    email: credentials.email,
                    password: credentials.password,
                    org_code: credentials.orgCode || undefined,
                }
            })
            return await completeLogin(response, credentials.orgCode)
        } catch (error) {
            return failLogin(error)
        }
    }

    // chooseLoginOrganization is the second step of a sign-in by an account
    // with several organizations.
    const chooseLoginOrganization = async (token: string, orgCode: string) => {
        setAuthState(prev => ({ ...prev, loading: true, error: null, errorCode: null }))
        try {
            const response = await request('/api/v1/auth/login/choose', 'POST', {
                body: { org_choice_token: token, org_code: orgCode },
            })
            return await completeLogin(response, orgCode)
        } catch (error) {
            return failLogin(error)
        }
    }

    // loginWithTokens establishes a session from tokens obtained via a passkey
    // or social-login (OAuth) flow. It mirrors the success branch of `login`
    // exactly (localStorage, API-client token, PostHog identify, state) so all
    // sign-in methods converge on the same authenticated state.
    const loginWithTokens = (tokens: SSOTokens): LoginResult => {
        const { access_token, refresh_token, user, expires_in, orgCode, default_org_code, organizations, mfa_enrollment_required } = tokens

        const newAuthState = {
            isAuthenticated: true,
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            loading: false,
            error: null,
            errorCode: null,
            expiresAt: new Date(Date.now() + expires_in * 1000),
            orgCode,
            showAuthErrorBanner: false,
            mfaEnrollmentRequired: mfa_enrollment_required === true,
        }

        localStorage.setItem('authState', JSON.stringify({
            isAuthenticated: newAuthState.isAuthenticated,
            user: newAuthState.user,
            accessToken: newAuthState.accessToken,
            refreshToken: newAuthState.refreshToken,
            expiresAt: newAuthState.expiresAt,
            orgCode: newAuthState.orgCode,
            mfaEnrollmentRequired: newAuthState.mfaEnrollmentRequired,
        }))

        setAuthToken(access_token)

        if (user?.uid) {
            posthog.identify(user.uid, { email: user?.email, name: user?.name, orgCode })
        }

        setAuthState(newAuthState)

        return {
            success: true,
            defaultOrgCode: default_org_code,
            hasNoOrgs: Array.isArray(organizations) && organizations.length === 0,
            mfaEnrollmentRequired: newAuthState.mfaEnrollmentRequired,
        }
    }

    const logout = async (orgCode?: string, options?: LogoutOptions) => {
        // Use provided orgCode or default to 'demo'
        const org = orgCode || 'demo'

        // Capture the current location BEFORE clearing state, so a session-expiry
        // logout can send the user back here after they log in again. Read the
        // live href from the browser (path + search + hash) so nothing is
        // mangled. Never preserve the login page itself.
        let preservedNext: string | undefined
        if (options?.preserveLocation) {
            const href = window.location.pathname + window.location.search + window.location.hash
            if (href && !href.startsWith(`/${org}/login`)) {
                preservedNext = href
            }
        }

        try {
            // Call the logout API endpoint if authenticated
            if (authState.accessToken) {
                await request('/api/v1/organizations/{orgCode}/auth/logout', 'POST', {
                    params: { path: { orgCode: org } },
                    body: {
                        refresh_token: authState.refreshToken
                    }
                })
            }
        } catch (error) {
            // Log the error but continue with logout process
            console.error('Logout API call failed:', error)
        }

        // Clear auth state
        setAuthState({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            loading: false,
            error: null,
            errorCode: null,
            expiresAt: null,
            orgCode: null,
            showAuthErrorBanner: false,
            mfaEnrollmentRequired: false,
        })
        sessionStorage.removeItem(PENDING_CHALLENGE_KEY)

        // Remove the access token from the API client
        removeAuthToken()

        // Reset PostHog identity
        posthog.reset()

        if (options?.redirect === false) return

        // Redirect to login page
        navigate({
            to: '/$org/login',
            params: { org },
            search: { next: preservedNext, email: undefined, password: undefined }
        })
    }

    const refreshAccessToken = async () => {
        if (!authState.refreshToken) {
            throw new Error('No refresh token available')
        }

        try {
            // A token rejected while another tab of this browser already
            // stored a newer session is retried with that one, never logged
            // out (spec 2026-10-11-37).
            const refreshed = await refreshWithSiblingFallback(
                (token) => callRefresh(apiClient.POST as never, authState.orgCode, token),
                authState.refreshToken,
                () => {
                    try {
                        return localStorage.getItem('authState')
                    } catch {
                        return null
                    }
                },
            )
            const response = refreshed.data

            const { access_token, refresh_token, expires_in, mfa_required, mfa_enrollment_required } = response as {
                access_token: string;
                refresh_token: string;
                expires_in: number;
                mfa_required?: boolean;
                mfa_enrollment_required?: boolean;
            }

            // The organization started requiring two-step verification
            // since this session opened (spec 2026-10-01-03). The session is
            // kept: the user confirms a code (or sets one up) and carries on.
            if (mfa_required) {
                sessionStorage.setItem(PENDING_CHALLENGE_KEY, JSON.stringify(
                    challengeFromLogin(response as Parameters<typeof challengeFromLogin>[0])
                ))
                goToMfaPage('mfa_required', authState.orgCode ?? undefined)
                return { success: true }
            }

            const newExpiresAt = expires_in ? new Date(Date.now() + expires_in * 1000) : authState.expiresAt

            setAuthState(prev => ({
                ...prev,
                accessToken: access_token,
                refreshToken: refresh_token || refreshed.refreshToken,
                expiresAt: newExpiresAt,
                showAuthErrorBanner: false,
                sessionEndedReason: null,
                mfaEnrollmentRequired: mfa_enrollment_required === true,
            }))
            if (mfa_enrollment_required) {
                setAuthToken(access_token)
                goToMfaPage('mfa_enrollment_required', authState.orgCode ?? undefined)
                return { success: true }
            }

            // Update the access token in the API client
            setAuthToken(access_token)

            return { success: true }
        } catch (error) {
            // A transient failure (network error, 502/503/504 while the backend
            // restarts) never logs out: keep the tokens, the caller retries.
            if (!(error instanceof RefreshRejectedError)) {
                throw error
            }
            // The server rejected the refresh token: the session is over.
            setAuthState(prev => ({
                ...prev,
                isAuthenticated: false,
                user: null,
                accessToken: null,
                refreshToken: null,
                expiresAt: null,
                showAuthErrorBanner: true,
                sessionEndedReason: sessionEndedReason(error),
            }))

            // Remove the access token from the API client
            removeAuthToken()

            throw error
        }
    }

    // Expose the refresh to code outside React (the document preview's
    // authenticated fetch, ensureAuthCookie in api/client.ts). The ref keeps
    // the registered function pointing at the latest closure, which reads the
    // current refresh token.
    const refreshRef = useRef(refreshAccessToken)
    useEffect(() => {
        refreshRef.current = refreshAccessToken
    })
    useEffect(() => {
        registerTokenRefresher(() => refreshRef.current())
        return () => registerTokenRefresher(null)
    }, [])

    const dismissAuthErrorBanner = () => {
        setAuthState(prev => ({
            ...prev,
            showAuthErrorBanner: false
        }))
    }

    const updateAuthUser = (userUpdate: Partial<AuthStateUser>) => {
        setAuthState(prev => {
            if (!prev.user) return prev
            const updatedUser = { ...prev.user, ...userUpdate, properties: { ...prev.user.properties, ...(userUpdate.properties || {}) } }
            return { ...prev, user: updatedUser }
        })
    }

    const updateAuthOrgCode = (orgCode: string) => {
        setAuthState(prev => (prev.orgCode === orgCode ? prev : { ...prev, orgCode }))
    }

    const applyUpgradedAccessToken = (accessToken: string, expiresIn: number) => {
        setAuthToken(accessToken)
        sessionStorage.removeItem(PENDING_CHALLENGE_KEY)
        const expiresAt = new Date(Date.now() + expiresIn * 1000)
        // Written synchronously too, so the route guards reading
        // localStorage see the lifted restriction at once.
        try {
            const stored = JSON.parse(localStorage.getItem('authState') || '{}')
            localStorage.setItem('authState', JSON.stringify({
                ...stored, accessToken, expiresAt, mfaEnrollmentRequired: false,
            }))
        } catch {
            // Rewritten by the persistence effect below anyway.
        }
        setAuthState(prev => ({ ...prev, accessToken, expiresAt, mfaEnrollmentRequired: false }))
    }

    // goToMfaPage sends the user to the full-screen two-step verification
    // page the error asks for, coming back here afterwards.
    const goToMfaPage = (code: MfaEventDetail['code'], org?: string) => {
        const path = window.location.pathname
        if (path.startsWith('/mfa-setup') || path.startsWith('/mfa-verify')) return
        const next = path + window.location.search + window.location.hash
        const target = org || authState.orgCode || path.split('/')[1] || 'demo'
        if (code === 'mfa_enrollment_required') {
            setAuthState(prev => (prev.mfaEnrollmentRequired ? prev : { ...prev, mfaEnrollmentRequired: true }))
        }
        navigate({
            to: code === 'mfa_enrollment_required' ? '/mfa-setup' : '/mfa-verify',
            search: { org: target, next },
        })
    }

    // The API client reports every MFA 403 (spec 2026-10-01-03): a session
    // restricted to enrollment, or an unverified session entering an
    // organization that requires two-step verification.
    useEffect(() => {
        const onMfa = (event: Event) => {
            const detail = (event as CustomEvent<MfaEventDetail>).detail
            if (!detail) return
            goToMfaPage(detail.code, detail.org)
        }
        window.addEventListener(MFA_EVENT, onMfa)
        return () => window.removeEventListener(MFA_EVENT, onMfa)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState.orgCode])

    const getLoginUrl = () => {
        const orgCode = authState.orgCode || 'demo'
        // Build `next` from the live browser href (path + search + hash) so the
        // full destination survives verbatim. Re-serialising `location.search`
        // through URLSearchParams mangled non-flat / array search values.
        const currentPath = window.location.pathname + window.location.search + window.location.hash
        const reason = authState.sessionEndedReason ? `&reason=${authState.sessionEndedReason}` : ''
        return `/${orgCode}/login?next=${encodeURIComponent(currentPath)}${reason}`
    }

    return (
        <AuthContext.Provider value={{
            ...authState,
            login,
            chooseLoginOrganization,
            loginWithTokens,
            logout,
            refreshAccessToken,
            dismissAuthErrorBanner,
            getLoginUrl,
            updateAuthUser,
            updateAuthOrgCode,
            applyUpgradedAccessToken,
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
} 